<template>
  <div class="view-startup-lander">
    <LandingHero :hero="hero" has-panel />
    <Container>
      <h2 class="pb-40 pb-lg-80">How it works</h2>
      <b-row>
        <b-col v-for="how in howItWorks" :key="how.id" md="4" class="mb-40 mb-md-0">
          <LandingNumberedCard :data="how" />
        </b-col>
      </b-row>
    </Container>
    <Container spacing="large" background-color="#fff" class="view-startup-lander__benefits">
      <h2 class="pb-40 pb-lg-80">
        Supercharge your hiring and scale your team
      </h2>
      <b-row>
        <b-col v-for="what in whatYouGet" :key="what.id" md="4" class="mb-60 mb-lg-60">
          <LandingBenefitCard :benefit="what" />
        </b-col>
      </b-row>
    </Container>
    <Container background-color="#F5F7FA">
      <h2 class="pb-30 pb-lg-60 pt-40 pt-lg-80">Trusted by top companies</h2>
      <LandingClientsGrid :clients="clients" />
    </Container>
    <Container background-color="#F5F7FA">
      <h2 class="pb-40 pb-lg-80">
        Teams that use us and why
      </h2>
      <LandingQuotesGrid :quotes="quotes" />
      <div class="pb-40 pb-60"></div>
    </Container>
    <Container spacing="large">
      <h2 class="pb-30 pb-lg-60">Frequently asked questions</h2>
      <Faq :faqs="faqs" />
    </Container>
  </div>
</template>

<script>
/* eslint nuxt/no-timing-in-fetch-data: 0 */

import LandingHero from '@/components/landing/LandingHero';
import Container from '@/components/Container';
import LandingNumberedCard from '@/components/landing/LandingNumberedCard';
import LandingBenefitCard from '@/components/landing/LandingBenefitCard';
import LandingClientsGrid from '@/components/landing/LandingClientsGrid';
import LandingQuotesGrid from '@/components/landing/LandingQuotesGrid';
import Faq from '@/components/Faq';
import { buildMetaHead } from '@/helpers/meta';
import { getFaqs } from '@/components/monetisation/MonetisationUtils.js';

export default {
  name: 'PageStartupsJoin',
  components: {
    LandingHero,
    Container,
    LandingNumberedCard,
    LandingBenefitCard,
    LandingClientsGrid,
    LandingQuotesGrid,
    Faq
  },
  data() {
    return {
      hero: {
        title: 'The #1 Nordic startup recruitment platform',
        subtitle: `
          Reach 550k+ startup-ready candidates and level up your hiring.
          Recruitment tools designed for startups and innovative tech companies -
          get everything set up in 5 minutes, <b>for free</b>.
        `,
        button: {
          link: '/client/startups/new',
          label: 'Start hiring now'
        },
        caption: 'Free. No CC Required',
        image: require('~/assets/components/startups/woman.png')
      },
      howItWorks: [
        {
          id: 1,
          no: '01',
          title: 'Build your employer brand',
          description:
            'Create an engaging startup profile to highlight your mission, values, culture, team, perks and more.'
        },
        {
          id: 2,
          no: '02',
          title: 'Post or import jobs',
          description:
            'Publish or import job posts to our startup-only job board and reach 550+ unique talent, 120k+ monthly active job seekers and 100+ partner job boards.'
        },
        {
          id: 3,
          no: '03',
          title: 'Achieve your hiring goals',
          description:
            'Thousands of people have been hired into key roles after applying on The Hub, from sales and marketing to product and engineering.'
        }
      ],
      whatYouGet: [
        {
          id: 1,
          image: require('../../assets/img/icons/user.svg'),
          title: '550k+ startup-ready candidates',
          description:
            'Every month we are visited by local and remote talent seeking to work in Nordic startups. Engineers, PMs, designers, sales, marketing and more.'
        },
        {
          id: 2,
          image: require('../../assets/img/icons/speaker.svg'),
          title: 'Job alert emails to 50k+ relevant candidates',
          description:
            'Once published, your job will instantly be sent to 50k+ relevant candidates, subscribed to digest awesome startup jobs daily or weekly.'
        },
        {
          id: 3,
          image: require('../../assets/img/icons/large-network.svg'),
          title: '100s of partner job boards',
          description:
            'For even greater reach, all jobs on The Hub are automatically distributed to our external partner network of 100s of premium global and local job boards.'
        },
        {
          id: 4,
          image: require('../../assets/img/icons/ats.svg'),
          title: 'Free applicant tracking system',
          description:
            'Use our free ATS or seamlessly integrate your own with just a few clicks. Source, evaluate and hire the best candidates, faster and easier.'
        },
        {
          id: 5,
          image: require('../../assets/img/icons/briefcase.svg'),
          title: 'Powerful integrations',
          description:
            'From plentiful ATS and Slack integrations to embedding your own personal career page, The Hub lets you streamline recruitment from A-Z.'
        },
        {
          id: 6,
          image: require('../../assets/img/icons/work-life.svg'),
          title: 'Founder friendly',
          description:
            'We provide truly unmatched pricing, designed for startups. Pre revenue/funding companies may even use The Hub for free through our <a href="/community-program" style="color:#4440DB; text-decoration:none">Community Program</a>.'
        }
      ],
      clients: [
        {
          image: 'startups/dk/wolt.png',
          url: '/startups/wolt-fi',
          name: 'Wolt'
        },
        { image: 'startups/dk/pleo.png', url: '/startups/pleo', name: 'Pleo' },
        {
          image: 'startups/se/karma.png',
          url: '/startups/karma',
          name: 'Karma'
        },
        {
          image: 'startups/no/kahoot.png',
          url: '/startups/kahoot',
          name: 'Kahoot'
        },
        {
          image: 'startups/dk/voi.png',
          url: '/startups/voi-technology',
          name: 'Voi'
        },
        {
          image: 'startups/se/truecaller.png',
          url: '/startups/truecaller',
          name: 'True caller'
        },
        {
          image: 'startups/fi/singa.png',
          url: '/startups/singa',
          name: 'Singa'
        },
        {
          image: 'startups/dk/templafy.png',
          url: '/startups/templafy',
          name: 'Templafy'
        },
        {
          image: 'startups/no/unacast.png',
          url: '/startups/unacast',
          name: 'Unacast'
        }
      ],
      quotes: [
        {
          quote:
            '"The Hub is the first place we go to reach the Copenhagen tech and startup community"',
          person: {
            image: 'dk/ben.jpg',
            name: 'Ben Posseti',
            position: 'Head of Marketing',
            company: 'Donkey Republic'
          }
        },
        {
          quote:
            '"What we really enjoy with The Hub is that we can post any position and know we will get relevant applicants"',
          person: {
            image: 'lena.jpg',
            name: 'Lena Bachmeyer',
            position: 'Head of HR',
            company: 'Vultus'
          }
        },
        {
          quote: '"The Hub helped our recruiting tremendously!"',
          person: {
            image: 'fi/ilona.png',
            name: 'Ilona Mooney',
            position: 'Founder & CEO',
            company: 'WorkAhead'
          }
        }
      ],
      // Remember to keep in sync with FAQ on /startups/faq
      faqs: getFaqs({ activeProducts: this.products })
    };
  },
  head() {
    return buildMetaHead({
      title: 'Get access to 100,000+ unique monthly job seekers',
      description:
        'We made recruitment easy so you can succeed faster. Post jobs, attract talents and manage all applicants in your user-friendly Applicant Management Tool.',
      path: this.$route.path,
      imageUrl: process.env.BASE_URL + '/images/thehub_2022_startup_join.jpg'
    });
  },
  computed: {
    products() {
      return this.$store.getters['meta/products'];
    }
  }
};
</script>

<style lang="scss">
.view-startup-lander {
  .hero {
    .hero-content {
      max-width: none;
    }

    .hero-pattern .man {
      left: 0;
      max-width: 361px;
    }
  }

  &__benefits {
    h3 {
      @include media-breakpoint-down(md) {
        font-size: rem(26px) !important;
      }
    }

    .card-benefit {
      &__description {
        @include media-breakpoint-down(md) {
          max-width: 100% !important;
        }
      }

      &__title {
        @include media-breakpoint-down(md) {
          max-width: 100% !important;
        }
      }
    }
  }
}
</style>
